import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import {
  HeartIcon,
  AcademicCapIcon,
  UsersIcon,
  SparklesIcon,
  LightBulbIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

const MissionStatement = () => {
  return (
    <>
      <Navbar />
      <main id="main-content" className="bg-white">

      {/* Hero Section */}
      <section className="section-padding band-light text-brand-text-primary pt-24 md:pt-32 relative overflow-hidden">
        <div className="content-container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="relative mb-8">
              <span className="inline-block bg-white shadow-sm text-brand-text-primary px-6 py-3 text-sm font-medium tracking-wider border border-brand-primary/20">
                Our Purpose
              </span>
            </div>
            <h1 className="text-display font-display mb-8 relative">
              <span className="relative">
                Mission Statement
              </span>
            </h1>
            <p className="text-xl leading-relaxed max-w-3xl mx-auto">
              Empowering students to become authors of their own futures
            </p>
          </div>
        </div>
      </section>

      {/* Mission Statement Section */}
      <section className="section-padding bg-white">
        <div className="content-container">
          <div className="max-w-4xl mx-auto">
            <div className="relative bg-gray-50 p-8 md:p-12 rounded-2xl mb-16 overflow-hidden">
              {/* Creative quote marks. Faded to 20% by design; the no-op gradient keeps
                  contrast checkers from reporting the decoration as unreadable text. */}
              <div aria-hidden="true" className="absolute top-2 md:top-4 left-4 md:left-8 text-4xl md:text-6xl font-display text-brand-accent/20" style={{ backgroundImage: 'linear-gradient(transparent, transparent)' }}>"</div>
              <div aria-hidden="true" className="absolute bottom-2 md:bottom-4 right-4 md:right-8 text-4xl md:text-6xl font-display text-brand-accent/20 transform rotate-180" style={{ backgroundImage: 'linear-gradient(transparent, transparent)' }}>"</div>

              <div className="relative z-10">
                <p className="text-lg md:text-xl text-body leading-relaxed text-center">
                  Our mission is to create a community of thinkers and doers who use their skills and agency to author their own futures and who strive for peace, equity, access, and dignity for all people. At the School of Authors, we will nurture all students' academic, physical, social and emotional growth and ensure their success through thoughtful consideration of each student's strengths and needs, holistic programming, an engaging curriculum that attends to the core pursuits of intellectualism, skill development, identity awareness, criticality of the world around us and joy in our everyday lives. In order to become the best that we can be, we embrace families and the East Harlem community as full partners in loving, celebrating and uplifting our children.
                </p>
              </div>

              {/* Subtle decorative elements */}
              <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-2 h-2 bg-brand-accent/30 rounded-full"></div>
            </div>

            {/* Core Values */}
            <div className="text-center mb-12">
              <span className="text-brand-accent-dark font-medium mb-4 block tracking-wide">Our Core Values</span>
              <h2 className="text-3xl md:text-4xl text-heading font-display mb-4">
                What Guides Us
              </h2>
              <div className="w-16 h-1 bg-brand-accent/30 mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
              <div className="text-center p-6 md:p-8 border border-gray-100 hover:border-brand-accent/30 transition-all duration-300 rounded-xl">
                <div className="w-16 h-16 bg-brand-accent/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <LightBulbIcon className="w-8 h-8 text-brand-accent" />
                </div>
                <h3 className="text-xl font-display text-heading mb-4">Intellectualism</h3>
                <p className="text-body">
                  Fostering critical thinking and a love for learning in all students.
                </p>
              </div>

              <div className="text-center p-6 md:p-8 border border-gray-100 hover:border-brand-accent/30 transition-all duration-300 rounded-xl">
                <div className="w-16 h-16 bg-brand-primary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <AcademicCapIcon className="w-8 h-8 text-brand-primary" />
                </div>
                <h3 className="text-xl font-display text-heading mb-4">Skill Development</h3>
                <p className="text-body">
                  Building competencies that empower students to achieve their goals.
                </p>
              </div>

              <div className="text-center p-6 md:p-8 border border-gray-100 hover:border-brand-accent/30 transition-all duration-300 rounded-xl">
                <div className="w-16 h-16 bg-brand-secondary/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <UsersIcon className="w-8 h-8 text-brand-secondary" />
                </div>
                <h3 className="text-xl font-display text-heading mb-4">Identity Awareness</h3>
                <p className="text-body">
                  Helping students understand and celebrate who they are.
                </p>
              </div>

              <div className="text-center p-6 md:p-8 border border-gray-100 hover:border-brand-accent/30 transition-all duration-300 rounded-xl">
                <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <ShieldCheckIcon className="w-8 h-8 text-purple-700" />
                </div>
                <h3 className="text-xl font-display text-heading mb-4">Criticality</h3>
                <p className="text-body">
                  Developing awareness of the world around us and our role in it.
                </p>
              </div>

              <div className="text-center p-6 md:p-8 border border-gray-100 hover:border-brand-accent/30 transition-all duration-300 rounded-xl">
                <div className="w-16 h-16 bg-yellow-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <SparklesIcon className="w-8 h-8 text-yellow-600" />
                </div>
                <h3 className="text-xl font-display text-heading mb-4">Joy</h3>
                <p className="text-body">
                  Finding happiness and celebration in our everyday lives.
                </p>
              </div>

              <div className="text-center p-6 md:p-8 border border-gray-100 hover:border-brand-accent/30 transition-all duration-300 rounded-xl">
                <div className="w-16 h-16 bg-pink-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <HeartIcon className="w-8 h-8 text-pink-600" />
                </div>
                <h3 className="text-xl font-display text-heading mb-4">Community Partnership</h3>
                <p className="text-body">
                  Embracing families and the East Harlem community as partners.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="section-padding bg-gray-50">
        <div className="content-container">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl text-heading font-display mb-6">
                Our Commitment
              </h2>
              <div className="w-16 h-1 bg-brand-accent/30 mx-auto mb-8"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white p-8 rounded-xl border-l-4 border-brand-primary shadow-sm">
                <h3 className="font-display text-heading text-xl mb-3">Holistic Growth</h3>
                <p className="text-body">
                  We nurture students' academic, physical, social, and emotional development through comprehensive programming.
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl border-l-4 border-brand-secondary shadow-sm">
                <h3 className="font-display text-heading text-xl mb-3">Individual Attention</h3>
                <p className="text-body">
                  Every student's unique strengths and needs are thoughtfully considered in their educational journey.
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl border-l-4 border-brand-accent shadow-sm">
                <h3 className="font-display text-heading text-xl mb-3">Equity & Justice</h3>
                <p className="text-body">
                  We strive for peace, equity, access, and dignity for all people in everything we do.
                </p>
              </div>

              <div className="bg-white p-8 rounded-xl border-l-4 border-purple-600 shadow-sm">
                <h3 className="font-display text-heading text-xl mb-3">Community Partnership</h3>
                <p className="text-body">
                  Families and the East Harlem community are embraced as full partners in loving and uplifting our children.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="section-padding band-warm text-brand-text-primary">
        <div className="content-container">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-display font-display mb-6">
              Join Our Community of Authors
            </h2>
            <p className="text-xl leading-relaxed mb-8">
              Discover how PS/MS 108 can help your child become a thinker, doer, and author of their own future.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <a
                href="/contact"
                className="inline-flex items-center justify-center bg-brand-primary text-white font-medium px-8 py-4 rounded-lg hover:bg-brand-navy transition-all duration-300"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

      </main>
      <Footer />
    </>
  );
};

export default MissionStatement;
